<script setup>
import ListingCard from '@components/ListingCard.vue'
import ListingRating from '@components/ListingRating.vue'
import {REVIEW_BENEFITS} from '@helpers/constants'
import {topOccurrences} from '@helpers/utils'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Navigation } from 'swiper/modules'
import 'swiper/css'
import Image from '@components/Image.vue'
import ratingLeft from '@images/rating-left.png'
import ratingRight from '@images/rating-right.png'

const props = defineProps({
  listings: {
    type: Array,
    required: true,
  },
  cta: {
    type: String,
    default: null,
  },
})

const productLineId = props.listings?.[0]?.product_line?.id
const modules = [Navigation]
const breakpoints = {
  768: {
    centeredSlides: false,
    spaceBetween: 24,
    slidesPerView: 3,
  },
}
const navigation = {
  nextEl: '.swiper-btn-next-' + productLineId,
  prevEl: '.swiper-btn-prev-' + productLineId,
}

const listingWithRatings = [...props.listings].filter(listing => Number(listing.total_reviews) > 0)
const totalRating = listingWithRatings.length > 0
  ? listingWithRatings.reduce((current, listing) => Number(listing.rating) + current, 0) / listingWithRatings.length
  : 0
const totalReviews = listingWithRatings.reduce((current, listing) => Number(listing.total_reviews) + current, 0)
const benefits = listingWithRatings.length > 0
  ? topOccurrences(listingWithRatings.flatMap(listing => listing.review_benefits), 3)
  : []
const mostRatedListing = listingWithRatings.sort((a, b) => b.total_reviews - a.total_reviews).shift()
</script>
<template>
  <div class="productLine">
    <div class="page-width">
      <div class="productLine__heading">
        <div v-if="totalReviews > 0" class="productLine__rating">
          <div class="productLine__rating-top">
            <Image :src="ratingLeft" alt="Rating Left" />
            <span>{{ Number(totalRating).toFixed(2) }}</span>
            <Image :src="ratingRight" alt="Rating Right" />
          </div>

          <a :href="mostRatedListing.url + '#reviews'">
            <ListingRating :total="totalReviews" :rating="totalRating" />
          </a>
        </div>
        <div class="productLine__content">
          <div class="productLine__title">{{ listings[0].product_line?.title }}</div>
          <div class="productLine__excerpt">{{ listings[0].product_line?.description }}</div>
        </div>
        <div v-if="totalReviews > 0" class="productLine__benefits">
          <div class="productLine__benefits-title">Top reported benefits</div>
          <div class="productLine__benefits-tags">
            <div v-for="benefit in benefits" class="productLine__benefits-tag">
              {{ REVIEW_BENEFITS?.[benefit] }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="productLine__listings">
      <div v-if="cta" class="productLine__cta">
        <Image :src="cta" :alt="listings[0].product_line?.title" loading="lazy" />
      </div>
      <div class="productLine__swiper">
        <div :class="'swiper-btn-prev swiper-btn-prev-' + productLineId">
          <svg xmlns="http://www.w3.org/2000/svg" width="11" height="17" viewBox="0 0 11 17" fill="none">
            <path d="M9.66683 1.14209L2.30225 8.50667L9.66683 15.8713" stroke="#8C8C8C" stroke-width="2.29545"/>
          </svg>
        </div>
        <swiper
          :modules="modules"
          :navigation="navigation"
          :loop="true"
          :centeredSlides="true"
          :spaceBetween="12"
          :slidesPerView="1.4"
          :breakpoints="breakpoints"
        >
          <swiper-slide v-for="listing in listings" :key="listing.id">
            <ListingCard class="collection__listing" :listing="listing" />
          </swiper-slide>
        </swiper>
        <div :class="'swiper-btn-next swiper-btn-next-' + productLineId">
          <svg xmlns="http://www.w3.org/2000/svg" width="10" height="17" viewBox="0 0 10 17" fill="none">
            <path d="M0.968913 15.8708L8.3335 8.50627L0.968915 1.14168" stroke="#8C8C8C" stroke-width="2.29545"/>
          </svg>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.productLine {
  margin-top: 30px;
  padding-bottom: 30px;

  @media screen and (min-width: 768px) {
    margin-top: 60px;
    padding-bottom: 60px;
  }

  .productLine__heading {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 18px;
    padding: 0 16px;

    @media screen and (min-width: 768px) {
      flex-direction: row;
      align-items: flex-start;
      gap: 48px;
      padding: 0;
    }

    .productLine__content {
      text-align: center;

      @media screen and (min-width: 768px) {
        order: 1;
      }

      .productLine__title {
        font-family: Outfit, 'Outfit Fallback', Arial, serif;
        color: var(--primary-600, #183D4F);
        font-size: 24px;
        font-weight: 700;
        line-height: 28px; /* 116.667% */

        @media screen and (min-width: 768px) {
          font-size: 40px;
          line-height: 48px;
        }
      }

      .productLine__excerpt {
        color: var(--Neutral-1000, #3C3833);
        font-family: Poppins, 'Poppins Fallback', 'Trebuchet MS Fallback', Arial, serif;
        font-size: 14px;
        font-weight: 500;
        line-height: 22px; /* 157.143% */
        margin-top: 8px;

        @media screen and (min-width: 768px) {
          font-size: 18px;
          line-height: 32px;
        }
      }
    }

    .productLine__rating {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 6px;

      @media screen and (min-width: 768px) {
        order: 2;
        gap: 8px;
      }

      .productLine__rating-top {
        display: flex;
        align-items: center;

        span {
          color: var(--Primary-600, #183D4F);
          font-family: Outfit, 'Outfit Fallback', Arial, serif;
          font-size: 24px;
          font-weight: 600;
          line-height: 31.93px; /* 133.041% */

          @media screen and (min-width: 768px) {
            font-size: 40px;
            line-height: 44px; /* 110% */
          }
        }

        img {
          height: 44px;

          @media screen and (min-width: 768px) {
            height: 60px;
          }
        }
      }
    }

    .productLine__benefits {
      text-align: center;

      @media screen and (min-width: 768px) {
        text-align: left;
        order: 3;
      }

      .productLine__benefits-title {
        color: var(--Light-navy, #183D4F);
        font-family: Outfit, 'Outfit Fallback', Arial, serif;
        font-size: 16px;
        font-weight: 600;
        margin-bottom: 10px;

        @media screen and (min-width: 768px) {
          font-size: 20px;
          margin-bottom: 18px;
        }
      }

      .productLine__benefits-tags {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-wrap: wrap;
        gap: 8px;

        @media screen and (min-width: 768px) {
          justify-content: flex-start;
        }

        .productLine__benefits-tag {
          padding: 1.931px 7.724px;
          border-radius: 5.149px;
          background: rgba(219, 126, 90, 0.10);
          color: var(--Orange---terracotta, #DB7E5A);
          font-family: Outfit, 'Outfit Fallback', Arial, serif;
          font-size: 14px;
          font-weight: 500;
          line-height: 15.448px; /* 110.345% */
          white-space: nowrap;

          @media screen and (min-width: 768px) {
            font-size: 16px;
          }
        }
      }
    }
  }

  .productLine__listings {
    display: flex;
    margin-top: 30px;
    gap: 24px;

    @media screen and (min-width: 768px) {
      margin-top: 40px;
      padding-top: 40px;
      border-top: 1px solid #C4C3C1;
    }

    .productLine__cta {
      flex-basis: 28%;
      border-radius: 16px;
      display: none;

      @media screen and (min-width: 768px) {
        display: flex;
      }

      img {
        border-radius: 16px;
        object-fit: cover;
        display: block;
        width: 100%;
        height: auto;
      }
    }

    .productLine__swiper {
      flex-basis: 100%;
      display: flex;
      align-items: center;
      gap: 24px;
      width: 100%;
      max-width: 100%;
      max-height: 100vh;
      // CSS Grid/Flexbox bug size workaround
      // @see https://github.com/kenwheeler/slick/issues/982
      // @see https://github.com/nolimits4web/swiper/issues/3599
      min-height: 0;
      min-width: 0;

      @media screen and (min-width: 768px) {
        flex-basis: 72%;
      }

      .swiper-btn-prev, .swiper-btn-next {
        flex-shrink: 0;
        cursor: pointer;
        display: none;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        opacity: 0.6;
        background: rgba(168, 139, 111, 0.15);
        width: 33px;
        height: 33px;

        @media screen and (min-width: 768px) {
          display: flex;
        }
      }

      :deep(.swiper) {
        flex: 1;
        
        .swiper-slide {
          border-radius: 12px;
          height: auto;
        }
      }

      :deep(.listing__button) {
        width: auto;
      }
    }
  }
}
</style>
